
import { defineComponent, computed, PropType } from "vue";
import { Doughnut as DoughnutChart } from "vue-chartjs";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from "chart.js";
import { GroupedAlert, AlertType } from "@/stores/telegramAlerts";

ChartJS.register(ArcElement, Tooltip, Legend);

export default defineComponent({
  name: "OperationsPieChart",
  components: {
    DoughnutChart,
  },
  props: {
    alerts: {
      type: Array as PropType<GroupedAlert[]>,
      required: true,
    },
    alertType: {
      type: String,
      default: "forex",
    },
  },
  setup(props) {
    const hasData = computed(() => {
      return props.alerts.some((a) => {
        const msg = a.mainMessage;
        return msg.pair || msg.index;
      });
    });

    // Custom plugin to draw labels on slices
    const customDataLabels = {
      id: "customDataLabels",
      afterDatasetsDraw(chart: any) {
        const { ctx } = chart;
        chart.data.datasets.forEach((dataset: any, i: number) => {
          const meta = chart.getDatasetMeta(i);
          meta.data.forEach((element: any, index: number) => {
            const value = dataset.data[index];
            if (value > 0) {
              const { x, y } = element.tooltipPosition();

              ctx.save();
              ctx.fillStyle = "white";
              ctx.font = "bold 12px Arial";
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              // Add shadow/outline for readability against colored backgrounds
              ctx.shadowColor = "rgba(0,0,0,0.7)";
              ctx.shadowBlur = 3;
              ctx.shadowOffsetX = 1;
              ctx.shadowOffsetY = 1;

              ctx.fillText(value, x, y);
              ctx.restore();
            }
          });
        });
      },
    };

    const chartOptions: ChartOptions<"doughnut"> = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: 10,
      },
      plugins: {
        legend: {
          position: "right",
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            font: {
              size: 10,
            },
          },
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.label || "";
              const value = context.parsed || 0;
              const total = context.chart._metasets[context.datasetIndex].total;
              const percentage = ((value / total) * 100).toFixed(1) + "%";
              return `${label}: ${value} (${percentage})`;
            },
          },
        },
      },
      cutout: "50%", // Reduce cutout slightly to give more room for slices/labels
    };

    const chartData = computed((): ChartData<"doughnut"> => {
      const counts: Record<string, number> = {};

      props.alerts.forEach((alert) => {
        const msg = alert.mainMessage;
        let key = "Desconocido";

        if (props.alertType === AlertType.FOREX) {
          if (msg.pair) key = msg.pair;
        } else if (props.alertType === AlertType.SYNTHETIC_INDICES) {
          if (msg.index) key = msg.index;
        } else {
          if (msg.pair) key = msg.pair;
          else if (msg.index) key = msg.index;
        }

        counts[key] = (counts[key] || 0) + 1;
      });

      // Sort by count descending
      const sortedEntries = Object.entries(counts).sort((a, b) => b[1] - a[1]);

      // Intentionally show all distinct assets without limiting or grouping them.
      // This matches the current UX requirement to keep every asset visible in the chart,
      // but be aware that a very large number of unique assets will produce many slices
      // and labels, which can impact readability and chart rendering performance.
      // Labels include the count, e.g. "EURUSD [4]", to make the absolute volume explicit.
      const finalLabels = sortedEntries.map((e) => `${e[0]} [${e[1]}]`);
      const finalData = sortedEntries.map((e) => e[1]);

      // Generate colors
      // Extended palette to handle more items without repeating too quickly
      const backgroundColors = [
        "#4e73df",
        "#1cc88a",
        "#36b9cc",
        "#f6c23e",
        "#e74a3b",
        "#858796",
        "#5a5c69",
        "#fd7e14",
        "#20c9a6",
        "#6f42c1",
        "#e83e8c",
        "#6610f2",
        "#fd77a4",
        "#17a2b8",
        "#28a745",
      ];

      // Cycle colors if we have more data points than colors
      const bgColors = finalLabels.map(
        (_, i) => backgroundColors[i % backgroundColors.length]
      );

      return {
        labels: finalLabels,
        datasets: [
          {
            data: finalData,
            backgroundColor: bgColors,
            hoverOffset: 4,
            borderWidth: 1,
            borderColor: "#fff",
          },
        ],
      };
    });

    return {
      chartData,
      chartOptions,
      hasData,
      plugins: [customDataLabels],
    };
  },
});
